import { useRef, useState } from 'react';
import {
  execActions, apiErrText as errText, rmbCents, CERT_STATE, CERT_STATES, TAX_PCT_MISSING_HINT, certStateZh as stateZh,
  type SubcontractRow, type AmendContractValueBody, type AmendContractValueResult,
  type ContractValueCandidate, type ContractValueAdjustment, type ApiError,
} from '../lib/api';

/**
 * 调整合同额 / Amend the contract value — 改改 9/21, on the live 华控 package.
 *
 * 「华控劳务合同已经签订了，只是合同额在中标价基础上有调整」, and the arithmetic behind it the same day:
 * 中标价 174,000.84 + 57.74㎡墙面金属饰面划分给劳务 62,936.60 − 无夜间施工扣减 17,440.00 = 219,497.44. Nothing
 * could move subcontracts.value_rmb once it existed, so the system kept planning and paying on the 议价 lock.
 *
 * The editor IS her sum: the 中标价 (read-only) ± signed 调整项 = a 合同额 prefilled from them but kept AS
 * WRITTEN ON THE PAPER. Her award reads 174,000.84 on paper and 174,000.85 here, so a cent of difference is a
 * neutral note, never a refusal. 预览 is a dry run of the exact body 确认 re-sends, and any edit clears it.
 *
 * Mounted twice: 验收标准与合同 (before signing — the .docx then prints the new figure) and 执行与结算. Like
 * RevertSignature it owns nothing but its own state; the PARENT invalidates its own query keys via onDone.
 * Money rule: the client adds up figures she typed, but every 含税 figure is the server's — nothing here
 * multiplies by a 税率.
 */

/** POST /subcontracts/:scId/contract-value — gm | budget, draft and signed alike ('agent' so the e2e rig can drive it). */
const AMEND_ROLES = new Set(['gm', 'budget', 'agent']);
export function canAmendContractValue(role: string | null | undefined): boolean {
  return AMEND_ROLES.has(role ?? '');
}

/** The route's own limits on 调整项 (routes/execution.ts parseAdjustments) — enforced here so they never 400. */
const MAX_ADJUSTMENTS = 20;
const ADJUSTMENT_LABEL_MAX = 200;

const round2 = (n: number) => Math.round(n * 100) / 100;
const cents = (n: number) => n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** 「+62,936.60」/「−17,440.00」 — a signed 调整项 the way she writes it (U+2212 for the minus). */
const signedAmount = (n: number) => `${n < 0 ? '−' : '+'}${cents(Math.abs(n))}`;
const signedRmb = (n: number) => `${n < 0 ? '−' : '+'}¥${cents(Math.abs(n))}`;

function CertStatePill({ state }: { state: string }) {
  const m = CERT_STATE[state] ?? { label: state, pill: 'pill-muted' };
  return <span className={`pill ${m.pill}`}>{m.label}</span>;
}

/** The 中标价 (议价 lock) and the contract value differ by at least a 分 — both 不含税, as stored. */
export function awardDiffers(sc: SubcontractRow): boolean {
  return sc.award_total_rmb != null && sc.value_rmb != null && Math.abs(sc.award_total_rmb - sc.value_rmb) > 0.005;
}

/**
 * 「中标价（议价锁定）含税 ¥A · 合同额（按签订版）含税 ¥B」 — shown when the two disagree and no amendment
 * explains why (a value typed at 生成分包合同, or 中信's legacy 变更). Both 含税 figures are the server's; with
 * no 税率 both fall back to 不含税 and say so.
 */
export function AwardVsContractLine({ sc }: { sc: SubcontractRow }) {
  if (!awardDiffers(sc)) return null;
  const incl = sc.award_total_incl_tax_rmb != null && sc.value_incl_tax_rmb != null;
  return (
    <div className="muted small" style={{ marginTop: 6, wordBreak: 'break-word' }}
      title="中标价 = 议价锁定的最终价；合同额 = 付款计划与付款所依据的金额 / award = the negotiated lock; contract value = what the plan and the payments run on">
      中标价（议价锁定）{incl ? `含税 ${rmbCents(sc.award_total_incl_tax_rmb)}` : rmbCents(sc.award_total_rmb)}
      {' · '}合同额{sc.status === 'signed' ? '（按签订版）' : ''}{incl ? `含税 ${rmbCents(sc.value_incl_tax_rmb)}` : rmbCents(sc.value_rmb)}
      {' / award vs contract value'}
      {!incl && <span style={{ color: 'var(--warn)' }}>（{TAX_PCT_MISSING_HINT}）</span>}
    </div>
  );
}

/**
 * The recorded amendment, read-only: 「中标价 A ± 调整项 = 合同额 B · 原因 · 谁 · 日期」, in the basis it was typed in.
 * A's 含税 twin is only on the subcontract (the server's, off the CURRENT lock), so it stands in only while
 * that lock is still the one the amendment was made against; otherwise A reads 「—」 rather than a guess.
 */
export function AmendmentBreakdown({ sc }: { sc: SubcontractRow }) {
  const am = sc.value_amendment;
  if (!am) return null;
  const incl = am.input_basis !== 'excl_tax';
  const sameLock = am.award_total_rmb != null && sc.award_total_rmb != null && Math.abs(am.award_total_rmb - sc.award_total_rmb) <= 0.005;
  const award = incl ? (sameLock ? sc.award_total_incl_tax_rmb ?? null : null) : am.award_total_rmb;
  const adj = am.adjustments ?? [];
  const expected = award != null ? round2(award + adj.reduce((t, a) => t + a.amount_rmb, 0)) : null;
  const diff = adj.length > 0 && expected != null && am.input_amount != null ? round2(am.input_amount - expected) : null;
  return (
    <div className="muted small" style={{ marginTop: 6, wordBreak: 'break-word' }}>
      <div>
        合同额调整（{incl ? '含税' : '不含税'}）/ amended value: 中标价 {rmbCents(award)}
        {adj.map((a, i) => <span key={i}>{' '}{a.amount_rmb < 0 ? '−' : '+'} {a.label} {rmbCents(Math.abs(a.amount_rmb))}</span>)}
        {adj.length > 0 ? ' = ' : ' → '}合同额 <strong style={{ color: 'var(--ink)' }}>{rmbCents(am.input_amount)}</strong>
        {diff != null && Math.abs(diff) > 0.005 && `（以签订版合同为准，与中标价 ± 调整项相差 ${signedRmb(diff)}）`}
      </div>
      <div>原因 / reason：{am.reason}{am.by ? ` · ${am.by}` : ''}{am.at ? ` · ${am.at.slice(0, 10)}` : ''}</div>
    </div>
  );
}

type Basis = 'incl' | 'ex';
type AdjLine = { key: number; label: string; amount: string };

export default function AmendContractValue({ sc, role, disabled, onDone }: {
  sc: SubcontractRow;
  /** me.account.role — renders only for the roles the route accepts (the server re-checks). */
  role: string | null | undefined;
  /** The parent's own busy flag: nothing here fires while another write on this contract is running. */
  disabled?: boolean;
  /** Parent-owned cache invalidation + message, awaited before the editor closes (the two mounts hold different keys). */
  onDone: (result: AmendContractValueResult) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [basis, setBasis] = useState<Basis>('incl');
  const [lines, setLines] = useState<AdjLine[]>([]);
  const nextKey = useRef(0);
  // Untouched, the total follows 中标价 ± 调整项; once typed it is the paper's figure and stays as typed.
  const [total, setTotal] = useState('');
  const [totalTouched, setTotalTouched] = useState(false);
  // Same for the reason: written from the 调整项 until she edits it herself.
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [busy, setBusy] = useState<null | 'preview' | 'confirm'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ContractValueCandidate[]>([]);
  // the dry-run answer AND the exact body that produced it — 确认 re-sends that body, never re-reads the inputs
  const [preview, setPreview] = useState<{ body: AmendContractValueBody; result: AmendContractValueResult } | null>(null);

  // hooks first, then the role gate — never return before the useState calls.
  if (!canAmendContractValue(role)) return null;

  const signed = sc.status === 'signed';
  const incl = basis === 'incl';
  const basisWord = incl ? '含税' : '不含税';
  const award = incl ? sc.award_total_incl_tax_rmb ?? null : sc.award_total_rmb ?? null;

  // A fully blank row is ignored; a half-filled one blocks the preview.
  const adjustments: ContractValueAdjustment[] = lines
    .filter((l) => l.label.trim() !== '' || l.amount.trim() !== '')
    .map((l) => ({ label: l.label.trim(), amount_rmb: l.amount.trim() === '' ? NaN : Number(l.amount) }));
  // non-zero AFTER rounding to the 分 — the server rounds each amount and refuses a zero line
  const linesValid = adjustments.every((a) => a.label !== '' && Number.isFinite(a.amount_rmb) && round2(a.amount_rmb) !== 0);
  const computed = award != null && linesValid ? round2(award + adjustments.reduce((t, a) => t + a.amount_rmb, 0)) : null;
  const totalStr = totalTouched ? total : computed != null ? computed.toFixed(2) : '';
  const amount = totalStr.trim() === '' ? NaN : Number(totalStr);
  const diff = computed != null && Number.isFinite(amount) ? round2(amount - computed) : null;
  const autoReason = linesValid && adjustments.length > 0
    ? `${signed ? '按签订版合同调整' : '签订前调整'}：${adjustments.map((a) => `${a.label} ${signedAmount(a.amount_rmb)}`).join('；')}`
    : '';
  const reasonStr = reasonTouched ? reason : autoReason;
  const canPreview = !busy && !disabled && linesValid && Number.isFinite(amount) && amount > 0 && reasonStr.trim() !== '';

  /** Any edit invalidates the preview — 确认 only ever re-sends a body that was just previewed. */
  function edited() { setPreview(null); setErr(null); setCandidates([]); }
  function close() {
    setOpen(false); setBasis('incl'); setLines([]); setTotal(''); setTotalTouched(false);
    setReason(''); setReasonTouched(false); edited();
  }
  function patchLine(key: number, p: Partial<AdjLine>) { setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l))); edited(); }
  function addLine() { setLines((ls) => [...ls, { key: nextKey.current++, label: '', amount: '' }]); edited(); }
  function removeLine(key: number) { setLines((ls) => ls.filter((l) => l.key !== key)); edited(); }
  /** incl_tax_unreachable answers a 含税 target, so a candidate is a reachable 含税 figure (and the basis follows it). */
  function pick(c: ContractValueCandidate) {
    setBasis('incl'); setTotal(c.value_incl_tax_rmb.toFixed(2)); setTotalTouched(true); edited();
  }

  function bodyOf(): AmendContractValueBody {
    const common = { reason: reasonStr.trim(), ...(adjustments.length > 0 ? { adjustments } : {}) };
    return incl ? { value_incl_tax_rmb: amount, ...common } : { value_rmb: amount, ...common };
  }

  async function run(body: AmendContractValueBody, dryRun: boolean) {
    setBusy(dryRun ? 'preview' : 'confirm'); setErr(null); setCandidates([]);
    try {
      const result = await execActions.amendContractValue(sc.id, dryRun ? { ...body, dry_run: true } : body);
      if (dryRun) { setPreview({ body, result }); return; }
      await onDone(result);
      close();
    } catch (e) {
      // every refusal carries a bilingual `detail` (which record blocks it, or the reachable amounts)
      setPreview(null);
      setErr(errText(e));
      const c = (e as ApiError)?.body?.candidates;
      if (Array.isArray(c)) {
        setCandidates(c.filter((x): x is ContractValueCandidate => typeof x?.value_rmb === 'number' && typeof x?.value_incl_tax_rmb === 'number'));
      }
    } finally { setBusy(null); }
  }

  if (!open) {
    return (
      <div style={{ marginTop: 6 }}>
        <button className="small" disabled={disabled} onClick={() => setOpen(true)}
          title={signed
            ? '签订版合同额与中标价不同？按纸质合同调整系统合同额，预付款、未放款的预付款凭证与付款计划随之重算 / the signed paper states a different amount? amend it here — the advance, an unreleased advance cert and the payment plan follow'
            : '签订前合同额有增减？在此按「中标价 ± 调整项」调整，生成的合同 Word 将写入新合同额 / scope changed before signing? amend the value here — the generated contract prints it'}>
          {signed ? '✎ 按签订版调整合同额 / Amend contract value' : '✎ 调整合同额 / Amend contract value'}
        </button>
      </div>
    );
  }

  const cert = preview?.result.advance_cert ?? null;
  const certMoved = cert != null && cert.from_state !== cert.to_state;
  const certStepsBack = certMoved && CERT_STATES.indexOf(cert!.to_state as any) < CERT_STATES.indexOf(cert!.from_state as any);

  return (
    <div className="card" style={{ marginTop: 8, marginBottom: 0, borderColor: 'var(--accent)' }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>
        {signed ? '按签订版调整合同额 / Amend the contract value to the signed paper' : '调整合同额（签订前）/ Amend the contract value before signing'}
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>
        中标价 ± 调整项 = 合同额，以签订版合同为准；先预览，再确认。
        {signed ? '预付款、未放款的预付款凭证与付款计划按新合同额（含税）重算。' : '生成的合同 Word 将写入新合同额，付款计划随之重算。'}
        {' / '}Award ± adjustments = contract value, as written on the paper. Preview, then confirm.
      </div>

      <div className="row-wrap small" style={{ gap: 14, marginBottom: 8 }}>
        <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
          <input type="radio" name={`cv-basis-${sc.id}`} checked={incl} disabled={!!busy} onChange={() => { setBasis('incl'); edited(); }} />
          <span>按含税金额 / tax-inclusive</span>
        </label>
        <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
          <input type="radio" name={`cv-basis-${sc.id}`} checked={!incl} disabled={!!busy} onChange={() => { setBasis('ex'); edited(); }} />
          <span>按不含税金额 / tax-exclusive</span>
        </label>
      </div>

      <div className="small" style={{ marginBottom: 6, wordBreak: 'break-word' }}>
        中标价（议价锁定）{basisWord} <strong>{rmbCents(award)}</strong>
        <span className="muted"> / award (negotiated lock), read-only</span>
      </div>

      {lines.map((l) => (
        <div key={l.key} className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginBottom: 6 }}>
          <div className="field grow" style={{ marginBottom: 0, minWidth: 200 }}>
            <label>调整项 / adjustment</label>
            <input value={l.label} disabled={!!busy} maxLength={ADJUSTMENT_LABEL_MAX} onChange={(e) => patchLine(l.key, { label: e.target.value })}
              placeholder="例如：57.74㎡墙面金属饰面划分给劳务 / e.g. wall cladding moved into scope" />
          </div>
          <div className="field" style={{ width: 190, marginBottom: 0 }}>
            <label>金额（{basisWord}，减项填负数）/ amount (± )</label>
            <input type="number" step="0.01" inputMode="decimal" value={l.amount} disabled={!!busy}
              onChange={(e) => patchLine(l.key, { amount: e.target.value })} placeholder="62936.60 / -17440" />
          </div>
          <button className="small" disabled={!!busy} onClick={() => removeLine(l.key)} title="删除该调整项 / remove">✕ 删除</button>
        </div>
      ))}
      <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
        <button className="small" disabled={!!busy || lines.length >= MAX_ADJUSTMENTS} onClick={addLine}
          title={lines.length >= MAX_ADJUSTMENTS ? `最多 ${MAX_ADJUSTMENTS} 项 / at most ${MAX_ADJUSTMENTS} lines` : undefined}>＋ 增加调整项 / Add adjustment</button>
        {!linesValid && <span className="small" style={{ color: 'var(--bad)' }}>每个调整项需填名称和非零金额 / each adjustment needs a label and a non-zero amount</span>}
      </div>

      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginTop: 10 }}>
        <div className="field" style={{ width: 250, marginBottom: 0 }}>
          <label>合同额（{basisWord}）· 以签订版合同为准 / as written on the contract</label>
          <input type="number" step="0.01" min={0} inputMode="decimal" value={totalStr} disabled={!!busy}
            onChange={(e) => { setTotal(e.target.value); setTotalTouched(true); edited(); }} />
        </div>
        {diff != null && Math.abs(diff) > 0.005 && (
          <span className="muted small" style={{ wordBreak: 'break-word' }}>
            与「中标价 ± 调整项」{rmbCents(computed)} 相差 {signedRmb(diff)}，以签订版合同为准
            {' / '}differs from award ± adjustments by {signedRmb(diff)} — the paper governs{' '}
            <button className="small" disabled={!!busy} onClick={() => { setTotal(''); setTotalTouched(false); edited(); }}>
              改用 {rmbCents(computed)} / use it
            </button>
          </span>
        )}
      </div>

      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
        <label>调整原因（必填）/ reason (required)</label>
        <input value={reasonStr} disabled={!!busy} onChange={(e) => { setReason(e.target.value); setReasonTouched(true); edited(); }}
          placeholder="例如：签订版合同第4.1条约定含税合同价 ¥… / e.g. clause 4.1 of the signed contract" />
      </div>

      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 8 }}>{err}</div>}
      {candidates.length > 0 && (
        <div className="row-wrap small" style={{ gap: 6, marginTop: 6, alignItems: 'center' }}>
          <span className="muted">可用金额 / reachable amounts:</span>
          {candidates.map((c) => (
            <button key={c.value_rmb} className="small" disabled={!!busy} onClick={() => pick(c)}>
              含税 {rmbCents(c.value_incl_tax_rmb)}（不含税 {rmbCents(c.value_rmb)}）
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div style={{ marginTop: 10 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>预览（尚未保存）· 税率 {preview.result.tax_pct}% / preview — nothing saved yet</div>
          <div className="cmp-wrap"><table className="cmp"><thead><tr>
            <th>项目 / item</th><th>调整前 / before</th><th>调整后 / after</th>
          </tr></thead><tbody>
            <tr>
              <td>合同额（含税）/ value (tax-incl.)</td>
              <td className="num">{rmbCents(preview.result.from.value_incl_tax_rmb)}</td>
              <td className="num"><strong>{rmbCents(preview.result.to.value_incl_tax_rmb)}</strong></td>
            </tr>
            <tr>
              <td>合同额（不含税）/ value (pre-tax)</td>
              <td className="num">{rmbCents(preview.result.from.value_rmb)}</td>
              <td className="num">{rmbCents(preview.result.to.value_rmb)}</td>
            </tr>
            {(preview.result.from.advance_amount_rmb != null || preview.result.to.advance_amount_rmb != null) && (
              <tr>
                <td>预付款（含税）/ advance (tax-incl.)</td>
                <td className="num">{rmbCents(preview.result.from.advance_amount_rmb)}</td>
                <td className="num">{rmbCents(preview.result.to.advance_amount_rmb)}</td>
              </tr>
            )}
            {cert && (
              <tr>
                <td>预付款凭证 / advance cert</td>
                <td className="num">{rmbCents(cert.from_amount)} <CertStatePill state={cert.from_state} /></td>
                <td className="num">{rmbCents(cert.to_amount)} <CertStatePill state={cert.to_state} /></td>
              </tr>
            )}
          </tbody></table></div>
          {certMoved && (
            <div className="small" style={{ color: 'var(--warn)', marginTop: 6 }}>
              ⚠ 预付款凭证 {stateZh(cert!.from_state)} → {stateZh(cert!.to_state)}{certStepsBack ? '，需重新推进' : ''}
              {' / '}the advance cert moves {cert!.from_state} → {cert!.to_state}{certStepsBack ? ' and must be pushed through again' : ''}
            </div>
          )}
          {/* 改改 9/21 (fb_8ef675e1) · the invoice was issued for the old figure — the server returns it on confirm */}
          {cert?.fapiao_returned && (
            <div className="small" style={{ color: 'var(--warn)', marginTop: 6 }}>
              ⚠ 确认后，预付款凭证所附发票将被退回，需按新金额重开 / confirming returns the attached invoice — it must be reissued for the new amount
            </div>
          )}
        </div>
      )}

      <div className="row-wrap" style={{ gap: 8, marginTop: 10 }}>
        <button className="small" disabled={!canPreview} onClick={() => void run(bodyOf(), true)}>
          {busy === 'preview' ? '预览中… / previewing…' : '预览 / Preview'}
        </button>
        <button className="btn-primary small" disabled={!!busy || !!disabled || !preview}
          title={preview ? undefined : '请先预览 / preview first'}
          onClick={() => { if (preview) void run(preview.body, false); }}>
          {busy === 'confirm' ? '调整中… / amending…' : '确认调整 / Confirm'}
        </button>
        <button className="small" disabled={!!busy} onClick={close}>取消 / Cancel</button>
      </div>
    </div>
  );
}
